import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar, User, FileText, Image as ImageIcon, Shield } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { Inspection } from '@/types/crm';
import { useState } from 'react';
import { Separator } from '@/components/ui/separator';
import { SignedPhotoImage } from './SignedPhotoImage';
import { UnifiedFilePreview } from '@/components/shared/UnifiedFilePreview';
import { normalizeStoragePath } from '@/lib/storageUtils';
import { useAuth } from '@/hooks/useAuth';

interface InspectionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  inspection: Inspection | null;
  onStatusUpdate?: (inspectionId: string, status: string) => void;
  onNotesUpdate?: (inspectionId: string, notes: string) => void;
}

const STATUS_OPTIONS = [
  { value: 'concept', label: 'Concept', color: 'bg-gray-500' },
  { value: 'ingediend', label: 'Ingediend', color: 'bg-blue-500' },
  { value: 'verwerkt', label: 'Verwerkt', color: 'bg-green-500' }
];

export function InspectionDetailModal({
  isOpen,
  onClose,
  inspection,
  onStatusUpdate,
  onNotesUpdate
}: InspectionDetailModalProps) {
  const { hasRole } = useAuth();
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState(inspection?.notes || '');
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);

  const isAdmin = hasRole('admin') || hasRole('super_admin') || hasRole('crm_user');
  const canEditStatus = isAdmin;

  if (!inspection) return null;

  const statusOption = STATUS_OPTIONS.find(s => s.value === inspection.status);
  const photoUrls = Array.isArray(inspection.photo_urls) ? inspection.photo_urls : [];

  const handleNotesSubmit = () => {
    if (onNotesUpdate) {
      onNotesUpdate(inspection.id, notes);
    }
    setEditingNotes(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Inspectie Details
            <Badge className={statusOption?.color}>
              {statusOption?.label}
            </Badge>
            {isAdmin && (
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                <Shield className="h-3 w-3 mr-1" />
                Admin toegang
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basis Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-muted-foreground">
                <FileText className="h-4 w-4" />
                Type
              </Label>
              <p className="font-medium">{inspection.type}</p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Datum
              </Label>
              <p className="font-medium">
                {format(new Date(inspection.date), 'PPP', { locale: nl })}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-muted-foreground">
                <User className="h-4 w-4" />
                Inspecteur
              </Label>
              <p className="font-medium">
                {inspection.inspector?.email || 'Onbekend'}
              </p>
            </div>

            {onStatusUpdate && canEditStatus && (
              <div className="space-y-2">
                <Label>Status Aanpassen</Label>
                <Select
                  value={inspection.status}
                  onValueChange={(value) => onStatusUpdate(inspection.id, value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Separator />

          {/* Project Info */}
          {inspection.project && (
            <div className="space-y-2">
              <Label className="text-muted-foreground">Project</Label>
              <p className="font-medium">{inspection.project.name}</p>
              {inspection.project.project_number && (
                <p className="text-sm text-muted-foreground">
                  {inspection.project.project_number}
                </p>
              )}
            </div>
          )}

          {/* Notities */}
          <div className="space-y-2">
            <Label>Notities</Label>
            {editingNotes ? (
              <div className="space-y-2">
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  placeholder="Notities over de inspectie..."
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleNotesSubmit}>
                    Opslaan
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingNotes(false);
                      setNotes(inspection.notes || '');
                    }}
                  >
                    Annuleren
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm whitespace-pre-wrap p-3 bg-muted rounded-md min-h-[100px]">
                  {inspection.notes || 'Geen notities'}
                </p>
                {onNotesUpdate && isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => setEditingNotes(true)}>
                    Bewerken
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Foto's */}
          {photoUrls.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Foto's ({photoUrls.length})
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {photoUrls.map((url, index) => (
                  <div
                    key={index}
                    className="aspect-square rounded-lg overflow-hidden border"
                  >
                    <SignedPhotoImage
                      photoRef={url}
                      alt={`Foto ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t">
            <p>Aangemaakt: {format(new Date(inspection.created_at), 'PPpp', { locale: nl })}</p>
            <p>Bijgewerkt: {format(new Date(inspection.updated_at), 'PPpp', { locale: nl })}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
